import { Grid2, TextField, Typography } from "@mui/material";
import { useEffect } from "react";
import { ErrorMessage, useFormikContext } from "formik";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { pattern } from "../../utils/schema/ValidationPattern.const";

const PackageDetailsForm = ({
  handleChange,
  handleBlur,
  values,
  touched,
  errors,
  setFieldTouched,
  setFieldValue,
}) => {
  const today = dayjs(new Date());
  const { isSubmitting, isValidating } = useFormikContext();

  useEffect(() => {
    if (isSubmitting && !isValidating) {
      let keys = Object.keys(errors);
      if (keys.length > 0) {
        const selector = `[name=${keys[0]}]`;
        const errorElement = document.querySelector(selector);
        if (errorElement) {
          errorElement.focus();
        }
      }
    }
  }, [errors, isSubmitting, isValidating]);

  const handleDecimalValidations = (e, fieldName) => {
    if (pattern.decimalRegex.test(e?.target?.value)) {
      const decimalValue = e?.target?.value?.toString()?.indexOf(".");
      const result = e?.target?.value?.toString()?.substring(decimalValue + 1);
      if (
        decimalValue !== -1 &&
        (Number(result) === 0 || Number(e?.target?.value) > 0.4) && e?.target?.value !== "0.0"
      ) {
        return e?.target?.value;
      } else {
        return decimalValue === -1 ? e?.target?.value : values[fieldName];
      }
    }
    return e?.target?.value === "" ? e?.target?.value : values[fieldName];
  };

  return (
    <div>
      <Typography sx={{ mb: 2 }} variant="h6">
        Package details:
      </Typography>

      <Grid2 container>
        <Grid2
          sx={{ minHeight: "80px" }}
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <Typography
            className="form-label required"
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              fontFamily: "poppins!important",
            }}
          >
            Package name
          </Typography>
          <Grid2>
            <TextField
              sx={{ borderRadius: "8px" }}
              name="packageName"
              fullWidth
              variant="outlined"
              size="small"
              type="text"
              placeholder="Enter Package Name"
              onChange={(e) =>
                handleChange({
                  target: {
                    name: "packageName",
                    value: e?.target?.value
                    ?.replace(/[^a-zA-Z0-9\s.,#\-/;'"_&]+/gi, "")
                    ?.replace(/\s\s+/g, " ")?.trimStart(),
                  },
                })
              }
              value={values?.packageName}
              slotProps={{ htmlInput: { maxLength: 30 } }}
              error={touched.packageName && Boolean(errors.packageName)}
            />
            <ErrorMessage
              name="packageName"
              component="div"
              className="error-messages"
            />
          </Grid2>
        </Grid2>
        <Grid2
          sx={{ minHeight: "80px" }}
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <Typography
            className="form-label required"
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              fontFamily: "poppins!important",
            }}
          >
            Description
          </Typography>
          <Grid2>
            <TextField
              name="description"
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter short description here "
              onChange={(e) =>
                handleChange({
                  target: {
                    name: "description",
                    value: e?.target?.value
                    ?.replace(/[^a-zA-Z0-9\s.,#\-/;'"_&]+/gi, "")
                    ?.replace(/\s\s+/g, " ")?.trimStart()
                  },
                })
              }
              value={values?.description}
              slotProps={{ htmlInput: { maxLength: 200 } }}
              error={touched.description && Boolean(errors.description)}
            />
            <ErrorMessage
              name="description"
              component="div"
              className="error-messages"
            />
          </Grid2>
        </Grid2>
        {/* Dimensions TextFields */}
        <Grid2
          container
          sx={{ display: "flex", flexDirection: "column", minHeight: "80px" }}
          spacing={0}
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              fontFamily: "poppins!important",
            }}
            className="form-label"
          >
            Dimensions (in inches)
          </Typography>
          <Grid2 spacing={2} size={{ xs: 12 }} sx={{ display: "flex", gap: 2 }}>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <TextField
                name="height"
                variant="outlined"
                size="small"
                placeholder="Height"
                fullWidth
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "height",
                      value: handleDecimalValidations(e, "height"),
                    },
                  })
                }
                onBlur={handleBlur}
                value={values?.height}
              />
              <ErrorMessage
                name="height"
                component="div"
                className="error-messages"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <TextField
                name="width"
                variant="outlined"
                size="small"
                placeholder="Width"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "width",
                      value: handleDecimalValidations(e, "width"),
                    },
                  })
                }
                onBlur={handleBlur}
                value={values.width}
                fullWidth
              />
              <ErrorMessage
                name="width"
                component="div"
                className="error-messages"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <TextField
                name="depth"
                variant="outlined"
                size="small"
                placeholder="Depth"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "depth",
                      value: handleDecimalValidations(e, "depth"),
                    },
                  })
                }
                onBlur={handleBlur}
                value={values.depth}
                fullWidth
              />
              <ErrorMessage
                name="depth"
                component="div"
                className="error-messages"
              />
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2
          sx={{ minHeight: "80px", alignItems: "baseline" }}
          container
          spacing={2}
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        >
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "poppins!important",
              }}
              className="form-label"
            >
              Weight (in lbs)
            </Typography>
            <TextField
              name="weight"
              variant="outlined"
              size="small"
              placeholder="Weight"
              onChange={(e) =>
                handleChange({
                  target: {
                    name: "weight",
                    value: handleDecimalValidations(e, "weight"),
                  },
                })
              }
              onBlur={handleBlur}
              value={values.weight}
              fullWidth
            />
            <ErrorMessage
              name="weight"
              component="div"
              className="error-messages"
            />
          </Grid2>
          <Grid2
            className="date-picker"
            size={{ xs: 12, sm: 12, md: 4, lg: 4 }}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Typography
              className="form-label required"
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "poppins!important",
              }}
            >
              Expiry date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="expiryDate"
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                slotProps={{
                  textField: {
                    error: touched?.expiryDate && Boolean(errors?.expiryDate),
                  },
                }}
                minDate={today}
                onChange={(newValue) => {
                  setFieldValue("expiryDate", newValue);
                }}
                onBlur={() => {
                  setFieldTouched("expiryDate", true, true);
                }}
                value={values.expiryDate ? dayjs(values.expiryDate) : null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={touched.expiryDate && Boolean(errors.expiryDate)}
                  />
                )}
              />
            </LocalizationProvider>
            {touched.expiryDate && Boolean(errors.expiryDate) ? (
              <span className="error-messages"> {errors.expiryDate}</span>
            ) : (
              ""
            )}
          </Grid2>
        </Grid2>
      </Grid2>
    </div>
  );
};

export default PackageDetailsForm;
